/**
 * The plot image, sides and direction of "Upload image + dimensions", kept
 * in sessionStorage under `schematic_image_<application number>` from the
 * Schematic Plan step's Next until the certificate step saves them.
 *
 * The Schematic Plan step comes before the certificate form, but these
 * values are certificate data (cp_saved_certificate_data), so they travel
 * the same way the drawing does (see schematicDrawingStore.ts). Only the
 * image's web address is kept, never the picture itself, so the certificate
 * request stays small.
 */

/** What the certificate form sends, under the names the backend saves. */
export type TSchematicImageValues = {
  plotShape: string;
  ab: number;
  bc: number;
  cd: number;
  da: number;
  /**
   * AA' and BB' in metres for the certificate's SCHEMATIC PLAN box; optional
   * like every plot variable, 0 when not entered.
   * BEFORE: aaBb: string; ("AA' and BB'" as one optional free-text box)
   */
  aa: number;
  bb: number;
  /** DE and EF in metres, for a plot with more sides; 0 when not entered. */
  de: number;
  ef: number;
  direction: string;
};

const keyFor = (applicationNumber: string | number | undefined) =>
  `schematic_image_${applicationNumber}`;

export function saveSchematicImageValues(
  applicationNumber: string | number | undefined,
  values: TSchematicImageValues
): void {
  sessionStorage.setItem(keyFor(applicationNumber), JSON.stringify(values));
}

/**
 * The values saved in this tab, or an empty object when there are none, so
 * the result can be spread into the certificate data as it is. A save
 * without them leaves what the backend already has.
 */
export function readSchematicImageValues(
  applicationNumber: string | number | undefined
): Partial<TSchematicImageValues> {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(keyFor(applicationNumber)) || "null");
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}
